import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePassport } from '../../../hooks/usePassport';

const PassportEntryPage = ({ setPageIndex }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	const { passport, existPassport } = usePassport();
	const [form, setForm] = useState({name: "", grade: ""});
    const [formErrors, setFormErrors] = useState({name: "", grade: ""});
    const [submitError, setSubmitError] = useState("");

    const { data } = useQuery({
		queryKey: ['member'],
		queryFn: async () => {
			const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
			return response.json();
		}
	});
	const member = data?.data ?? null;

	useEffect(() => {
		if (member) {
			setForm({name: member.memberChildName || "", grade: member.memberGrade || ""});
		}
	}, [member]);

    const mutation = useMutation({
		mutationFn: async (data: { name: string, grade: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.create_passport.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
					case "UNAUTHORIZED":
						setSubmitError("인증된 사용자가 아닙니다.");
						break;
					case "NOT_FOUND_MEMBER":
						setSubmitError("인증된 사용자가 아닙니다.");
						break;
					case "EXIST_PASSPORT":
						setSubmitError("이미 여권이 있습니다.");
						break;
					case "INVALID_REQUEST":
						setSubmitError("필수 필드값이 입력되지 않았습니다.");
						break;
					default :
						setSubmitError("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(!response.data) {
				setSubmitError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data.code) {
				queryClient.invalidateQueries({ queryKey: ['passport'] });
				setPageIndex(2);
			}
		},
		onError: (error: any) => {
			setSubmitError(error.message);
		}
	});

    const validate = (name: string, value: string) => {
		let validValue = value;
		let errorMsg = "";

		if (name === "name") {
			if (!value.trim()) {
				errorMsg = "이름을 입력해주세요.";
			} else if (/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g.test(value)) {
				errorMsg = "이름은 한글, 영문만 입력 가능합니다.";
			} else if (value.length < 2) {
				errorMsg = "이름은 2자 이상 입력해주세요.";
			}
		}

		if (name === "grade") {
			if (!value.trim()) {
				errorMsg = "학년을 선택해주세요.";
			}
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	};

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const result = validate(name, value);
		setSubmitError("");
		setForm(prev => ({ ...prev, [name]: result.value }));
		setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const handleSubmit = () => {
		const fields = ["name", "grade"];
		const newErrors: any = {};
		const newForm: any = { ...form };

		fields.forEach((field) => {
			const result = validate(field, form[field], form);
			newErrors[field] = result.error;
			newForm[field] = result.value;
		});

		setFormErrors(newErrors);

		if (Object.values(newErrors).some((e) => e)) return;

		mutation.mutate(newForm);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleSubmit();
	};

	return (
		<div className="bg-transparent md:bg-gradient-to-br md:from-blue-900 md:to-blue-600 rounded-none md:rounded-[20px] px-5 py-12 pb-24 md:px-8 md:py-16 md:pb-28 md:px-10 md:py-20 md:pb-32">
			{ existPassport && (
				<div className="absolute top-5 right-5 bg-white/20 px-4 py-2 rounded-full border-2 border-white font-bold text-white">
					LEVEL <span>{passport.level}</span>
				</div>
			)}
			<div className="w-[120px] h-[120px] bg-white/20 border-[3px] border-white rounded-full flex items-center justify-center text-[60px] mx-auto mb-8 mt-3">🌍</div>
			<h1 className="text-5xl font-black mb-4 text-white">AI 탐험 여권</h1>
			<p className="text-xl mb-8 opacity-90 text-white">ARTIFICIAL INTELLIGENCE PASSPORT</p>
			{ !existPassport && (
				<div className="bg-white/20 backdrop-blur-md p-10 rounded-[20px] max-w-[400px] mx-auto">
					<input
						type="text"
						name="name"
						id="name"
						value={member?.memberChildName}
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						disabled={mutation.isPending}
						placeholder="이름을 입력하세요"
						className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
					/>
					{formErrors.name && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{formErrors.name}
						</p>
					)}
					<div className="relative mt-3">
						<select
							type="text"
							name="grade"
							id="grade"
							value={form.grade}
							onChange={handleChange}
							disabled={mutation.isPending}
							className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
						>
							<option value="">학년을 선택해주세요</option>
							<option value="1">초등 1학년</option>
							<option value="2">초등 2학년</option>
							<option value="3">초등 3학년</option>
							<option value="4">초등 4학년</option>
							<option value="5">초등 5학년</option>
							<option value="6">초등 6학년</option>
						</select>
						<i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
					</div>
					{formErrors.grade && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{formErrors.grade}
						</p>
					)}
					<button
						onClick={handleSubmit}
						onKeyDown={handleKeyDown}
						disabled={mutation.isPending}
						className={`w-full px-8 py-4 rounded-full font-bold hover:bg-yellow-300 mt-3 transition
							${mutation.isPending ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-yellow-400 text-blue-900 "}
						`}
					>
						{mutation.isPending ? "여권 만드는 중..." : "여행 시작하기 ✈️"}
					</button>
					{submitError && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{submitError}
						</p>
					)}
				</div>
			)}
			{ existPassport && (
				<div className="bg-white/20 backdrop-blur-md p-10 rounded-[20px] max-w-[400px] mx-auto">
					<div class="text-2xl font-bold mb-1 text-white">
						{passport.name}
					</div>
					<div className="text-m mb-1 text-white">
						(초등 {passport.grade}학년)
					</div>
					<div className="text-sm mt-4 mb-1 text-white">
						여권번호: {passport.code}
					</div>
					<div class="text-sm opacity-75 text-white">
						발급일: {passport.datetime.split(' ')[0]}
					</div>
				</div>
			)}
		</div>
	);
}

export default PassportEntryPage;